import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Loader2 } from "lucide-react";

/**
 * YourBeginning — one-time post-signup onboarding.
 * Route: /onboarding/your-beginning
 *
 * Flow: 5 questions, one per viewport, elegant transitions.
 *   1. DATE OF BIRTH
 *   2. BIRTH TIME         (with "I don't know" option)
 *   3. BIRTH PLACE        (resolved server-side to lat/lon/tz)
 *   4. GENDER
 *   5. MARITAL STATUS
 *
 * After save → /onboarding/your-beginning/complete → /app.
 */

const INK = "#1C1C1C";
const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const MUTE = "#676773";

const GENDERS = [
  { id: "male",        label: "Male" },
  { id: "female",      label: "Female" },
  { id: "other",       label: "Other / Self-describe" },
  { id: "prefer_not",  label: "Prefer not to say" },
];

const MARITAL = [
  { id: "single",          label: "Single" },
  { id: "in_relationship", label: "In a relationship" },
  { id: "engaged",         label: "Engaged" },
  { id: "married",         label: "Married" },
  { id: "separated",       label: "Separated" },
  { id: "divorced",        label: "Divorced" },
  { id: "widowed",         label: "Widowed" },
  { id: "prefer_not",      label: "Prefer not to say" },
];

export default function YourBeginning() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [step, setStep] = useState(0); // 0..5 (5 = success)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [dob, setDob] = useState("");
  const [timeKnown, setTimeKnown] = useState(true);
  const [tob, setTob] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [gender, setGender] = useState("");
  const [marital, setMarital] = useState("");
  const [showWhy, setShowWhy] = useState(false);

  // Not signed in → bounce
  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  // If profile already complete, skip onboarding
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/profile");
        if (r.data?.profile_complete) navigate("/app/chat", { replace: true });
      } catch (_) { /* ignore */ }
    })();
  }, [user, navigate]);

  const canAdvance = useMemo(() => {
    if (step === 0) return !!dob;
    if (step === 1) return !!tob;  // §Strict — exact time is required
    if (step === 2) return !!city && city.trim().length >= 2;
    if (step === 3) return !!gender;
    if (step === 4) return !!marital;
    return false;
  }, [step, dob, tob, timeKnown, city, gender, marital]);

  const next = () => { if (canAdvance) { setError(""); setStep((s) => s + 1); } };
  const back = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        date_of_birth: dob,
        time_of_birth: tob,                // §Strict — always sent
        birth_time_known: true,
        birth_city: city.trim(),
        birth_country: country.trim() || null,
        gender,
        marital_status: marital,
      };
      await api.post("/onboarding/your-beginning", payload);
      setStep(5); // success screen
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not save.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Advance from step 4 submits automatically
  useEffect(() => {
    if (step === 4 && marital && !saving) {
      // slight delay to let the chip tap finish
      const t = setTimeout(submit, 180);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marital]);

  if (loading || !user) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center" style={{ background: "#FAFAFC" }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: INDIGO }} />
      </div>
    );
  }

  const PROGRESS_TOTAL = 5;

  return (
    <div
      className="relative min-h-[100dvh] flex flex-col"
      style={{
        background: "#FAFAFC",
        backgroundImage: "radial-gradient(#DCE2EA 1px, transparent 1px)",
        backgroundSize: "28px 28px",
        color: INK,
      }}
      data-testid="your-beginning"
    >
      {/* top bar */}
      <header className="flex items-center justify-between px-6 sm:px-10 pt-6">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.4em]" style={{ color: INDIGO }}>
          <span style={{ textShadow: `0 0 10px ${INDIGO}66` }}>✦</span>
          <span>KALKI</span>
        </div>
        {step > 0 && step < 5 && (
          <button
            onClick={back}
            data-testid="yb-back"
            className="inline-flex items-center gap-1 text-[11px] tracking-[0.3em] opacity-55 hover:opacity-90 transition-opacity"
            style={{ color: MUTE }}
          >
            <ChevronLeft className="w-3 h-3" /> BACK
          </button>
        )}
      </header>

      {/* Progress — subtle 5-dot indicator */}
      {step < 5 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {Array.from({ length: PROGRESS_TOTAL }).map((_, i) => (
            <span
              key={i}
              className="rounded-full transition-all"
              style={{
                width: i === step ? 24 : 6,
                height: 6,
                background: i <= step
                  ? `linear-gradient(90deg, ${INDIGO}, ${FUCHSIA})`
                  : "rgba(28,28,28,0.12)",
              }}
            />
          ))}
        </div>
      )}

      {/* Main stage */}
      <main className="flex-1 flex flex-col items-center justify-center w-full max-w-[560px] mx-auto px-6 sm:px-10 pb-28">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <StepFrame key="s0" title="Your Beginning" eyebrow="Step 1 of 5" question="When did your journey begin?" subtitle="Your date of birth.">
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full text-center text-[22px] tracking-[0.02em] py-4 bg-transparent outline-none"
                style={{ borderBottom: `1px solid ${INDIGO}22`, color: INK, letterSpacing: "0.04em" }}
                data-testid="yb-dob"
              />
            </StepFrame>
          )}

          {step === 1 && (
            <StepFrame key="s1" eyebrow="Step 2 of 5" question="Do you know the time you were born?" subtitle="Exact birth time refines Vedic timing.">
              <input
                type="time"
                value={tob}
                onChange={(e) => { setTob(e.target.value); setTimeKnown(true); }}
                className="w-full text-center text-[22px] tracking-[0.02em] py-4 bg-transparent outline-none"
                style={{ borderBottom: `1px solid ${INDIGO}22`, color: INK, letterSpacing: "0.04em" }}
                data-testid="yb-tob"
              />
              <div className="mt-5 text-[11px] tracking-[0.26em] uppercase text-center" style={{ color: MUTE, opacity: 0.7 }}>
                Exact time is required · ask a family member if unsure
              </div>
            </StepFrame>
          )}

          {step === 2 && (
            <StepFrame key="s2" eyebrow="Step 3 of 5" question="Where did your journey begin?" subtitle="Your birth city.">
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Ahmedabad"
                className="w-full text-center text-[22px] py-4 bg-transparent outline-none"
                style={{ borderBottom: `1px solid ${INDIGO}22`, color: INK }}
                data-testid="yb-city"
                autoFocus
              />
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country (optional)"
                className="mt-4 w-full text-center text-[14px] py-3 bg-transparent outline-none opacity-80"
                style={{ borderBottom: `1px solid ${INDIGO}18`, color: MUTE }}
                data-testid="yb-country"
              />
            </StepFrame>
          )}

          {step === 3 && (
            <StepFrame key="s3" eyebrow="Step 4 of 5" question="How would you like KALKI to know you?" subtitle="This stays private to you.">
              <div className="flex flex-col gap-3 w-full mt-2">
                {GENDERS.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    data-testid={`yb-gender-${g.id}`}
                    onClick={() => { setGender(g.id); setTimeout(() => setStep((s) => Math.max(s, 4)), 180); }}
                    className="w-full py-4 rounded-full text-[14.5px] font-medium transition-all"
                    style={{
                      background: gender === g.id ? `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})` : "white",
                      border: gender === g.id ? "1px solid transparent" : "1px solid rgba(28,28,28,0.09)",
                      color: gender === g.id ? "white" : INK,
                      boxShadow: gender === g.id ? `0 10px 28px ${INDIGO}33` : "0 2px 10px rgba(30,20,70,0.04)",
                    }}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </StepFrame>
          )}

          {step === 4 && (
            <StepFrame key="s4" eyebrow="Step 5 of 5" question="Where are you in your relationship journey?" subtitle="KALKI will learn more through conversation.">
              <div className="grid grid-cols-2 gap-3 w-full mt-2">
                {MARITAL.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`yb-marital-${m.id}`}
                    onClick={() => setMarital(m.id)}
                    disabled={saving}
                    className="py-3.5 rounded-full text-[13.5px] font-medium transition-all"
                    style={{
                      background: marital === m.id ? `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})` : "white",
                      border: marital === m.id ? "1px solid transparent" : "1px solid rgba(28,28,28,0.09)",
                      color: marital === m.id ? "white" : INK,
                      boxShadow: marital === m.id ? `0 10px 28px ${INDIGO}33` : "0 2px 10px rgba(30,20,70,0.04)",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {saving && (
                <div className="mt-6 flex items-center gap-2 text-[12px] tracking-[0.26em]" style={{ color: INDIGO }}>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  SAVING YOUR BEGINNING…
                </div>
              )}
            </StepFrame>
          )}

          {step === 5 && (
            <SuccessFrame
              key="s5"
              onContinue={() => navigate("/app")}
            />
          )}
        </AnimatePresence>

        {error && (
          <div className="mt-6 text-[12.5px] text-center" style={{ color: FUCHSIA }} data-testid="yb-error">
            {error}
          </div>
        )}

        {/* Primary CTA for steps 0, 1, 2 (3 and 4 auto-advance) */}
        {step < 3 && (
          <button
            onClick={next}
            disabled={!canAdvance}
            data-testid="yb-continue"
            className="mt-10 inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full text-[13px] font-semibold tracking-[0.14em] uppercase disabled:opacity-30 transition-all"
            style={{
              background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
              color: "white",
              boxShadow: `0 16px 42px ${INDIGO}44`,
              minWidth: 220,
            }}
          >
            Continue →
          </button>
        )}
      </main>

      {/* Why do you need this */}
      {step < 5 && (
        <footer className="pb-6 flex flex-col items-center" style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}>
          <button
            onClick={() => setShowWhy((v) => !v)}
            className="text-[11px] tracking-[0.26em] uppercase opacity-55 hover:opacity-90"
            style={{ color: MUTE }}
            data-testid="yb-why"
          >
            Why do you need this?
          </button>
          <AnimatePresence>
            {showWhy && (
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                className="mt-3 max-w-[420px] text-center text-[12.5px] leading-[1.6] px-6"
                style={{ color: MUTE }}
              >
                Your date, time and place of birth are used to calculate your Vedic chart and timing.
                Your personal context helps KALKI make the conversation more relevant to your life.
                These details stay private to your KALKI account.
              </motion.p>
            )}
          </AnimatePresence>
        </footer>
      )}
    </div>
  );
}

/* ---------- Sub-components ---------- */
function StepFrame({ eyebrow, question, subtitle, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="w-full text-center"
    >
      {eyebrow && (
        <div className="text-[10px] tracking-[0.36em] uppercase mb-5" style={{ color: MUTE }}>
          ✦ {eyebrow}
        </div>
      )}
      <h2
        className="font-serif font-light leading-[1.08] mb-3"
        style={{ fontSize: "clamp(28px, 4.6vmin, 44px)", color: INK }}
      >
        {question}
      </h2>
      {subtitle && (
        <p className="text-[13.5px] mb-10" style={{ color: MUTE }}>
          {subtitle}
        </p>
      )}
      <div className="w-full flex flex-col items-center">{children}</div>
    </motion.div>
  );
}

function SuccessFrame({ onContinue }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="w-full text-center"
      data-testid="yb-success"
    >
      <div className="text-[11px] tracking-[0.4em]" style={{ color: INDIGO, textShadow: `0 0 10px ${INDIGO}66` }}>✦ KALKI</div>
      <h2
        className="font-serif font-light leading-[1.05] mt-6 mb-4"
        style={{ fontSize: "clamp(32px, 6vmin, 60px)", color: INK }}
      >
        Your beginning<br />
        <em
          className="not-italic"
          style={{ background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`, WebkitBackgroundClip: "text", color: "transparent" }}
        >
          is now part of our journey.
        </em>
      </h2>
      <p className="mt-5 max-w-[36ch] mx-auto text-[14.5px] leading-[1.65]" style={{ color: MUTE }}>
        I can now understand your Vedic timing with greater context.
      </p>
      <button
        onClick={onContinue}
        data-testid="yb-enter-kalki"
        className="mt-10 inline-flex items-center gap-2 px-10 py-4 rounded-full text-[13.5px] font-semibold tracking-[0.14em] uppercase"
        style={{
          background: `linear-gradient(135deg, ${INDIGO}, ${FUCHSIA})`,
          color: "white",
          boxShadow: `0 20px 50px ${INDIGO}55`,
          minWidth: 240,
        }}
      >
        Talk to KALKI →
      </button>
    </motion.div>
  );
}
